import * as React from "react";
import { Form, NavLink, Link } from "react-router";
import { Logo } from "../branding/Logo";
import { Button } from "../ui/Button";
import { textoVigencia } from "../../auth";
import { ADMIN_NAV } from "./navLinks";
import type { NavLinkItem, NavChild, NavGroup } from "./navLinks";

function isNavGroup(child: NavChild): child is NavGroup {
  return "children" in child;
}

export type HeaderLink = NavLinkItem;

export type HeaderProps = {
  links: HeaderLink[];
  user?: {
    name: string;
    avatarUrl?: string | null;
    daysLeft?: number | null;
    hoursLeft?: number | null;
    minutesLeft?: number | null;
    isAdmin?: boolean;
  };
};

const ChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

/** Campanita de notificaciones con badge de no leídas. Solo para usuarios autenticados. */
/** Acceso a "Invita a un amigo" (sistema de referidos). */
function ReferidosLink() {
  return (
    <Link
      to="/referidos"
      aria-label="Invita a un amigo"
      title="Invita a un amigo y gana días"
      className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-rose-50 text-rose-600 transition-colors hover:bg-rose-100"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <path d="M10.5 19h-5.5a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v4" />
        <path d="M3 7l9 6l2.983 -1.989l6.017 -4.011" />
        <path d="M18 22l3.35 -3.284a2.143 2.143 0 0 0 .005 -3.071a2.242 2.242 0 0 0 -3.129 -.006l-.224 .22l-.223 -.22a2.242 2.242 0 0 0 -3.128 -.006a2.143 2.143 0 0 0 -.006 3.071l3.355 3.296z" />
      </svg>
    </Link>
  );
}

function NotificationBell() {
  const [noLeidas, setNoLeidas] = React.useState(0);

  React.useEffect(() => {
    let cancelado = false;
    fetch("/api/notificaciones/no-leidas")
      .then((r) => (r.ok ? r.json() : { noLeidas: 0 }))
      .then((d) => { if (!cancelado) setNoLeidas(Number(d?.noLeidas) || 0); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, []);

  return (
    <Link
      to="/notificaciones"
      aria-label={noLeidas > 0 ? `Notificaciones (${noLeidas} sin leer)` : "Notificaciones"}
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-700 transition-colors hover:bg-gray-50"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
      {noLeidas > 0 && (
        <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold leading-none text-white">
          {noLeidas > 9 ? "9+" : noLeidas}
        </span>
      )}
    </Link>
  );
}

export function Header({ links, user }: HeaderProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileDropdown, setMobileDropdown] = React.useState<string | null>(null);
  const [mobileSubDropdown, setMobileSubDropdown] = React.useState<string | null>(null);
  const [mobileAdminSection, setMobileAdminSection] = React.useState<string | null>(null);
  const [avatarError, setAvatarError] = React.useState(false);

  // Menús desplegables de escritorio: controlados por estado para que sean
  // accesibles por clic/teclado (además del hover para ratón).
  const [desktopMenu, setDesktopMenu] = React.useState<string | null>(null);
  const [desktopSubMenu, setDesktopSubMenu] = React.useState<string | null>(null);
  const desktopNavRef = React.useRef<HTMLElement>(null);
  // Flyouts de nivel 3: se les fija el alto máximo al espacio real disponible bajo cada uno,
  // así el scroll solo aparece cuando el submenú no cabe en pantalla.
  const subFlyoutRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  React.useEffect(() => {
    if (!desktopSubMenu) return;
    const el = subFlyoutRefs.current[desktopSubMenu];
    if (!el) return;
    const espacio = window.innerHeight - el.getBoundingClientRect().top - 16;
    el.style.maxHeight = espacio > 0 ? `${espacio}px` : "";
  }, [desktopSubMenu]);

  const closeDesktop = React.useCallback(() => {
    setDesktopMenu(null);
    setDesktopSubMenu(null);
  }, []);

  // Cerrar con Escape o al hacer clic fuera de la navegación.
  React.useEffect(() => {
    if (desktopMenu === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeDesktop();
    }
    function onPointer(e: MouseEvent) {
      if (desktopNavRef.current && !desktopNavRef.current.contains(e.target as Node)) {
        closeDesktop();
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [desktopMenu, closeDesktop]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileDropdown(null);
    setMobileSubDropdown(null);
    setMobileAdminSection(null);
  };

  function toggleMobileDropdown(label: string) {
    setMobileDropdown(prev => {
      if (prev === label) { setMobileSubDropdown(null); return null; }
      setMobileSubDropdown(null);
      return label;
    });
  }

  return (
    <header className="sticky top-0 z-50 border-b border-gray-100 bg-white">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <a href="/" className="flex items-center gap-2">
          <Logo className="h-7 w-auto" />
        </a>

        {/* Desktop nav */}
        <nav
          ref={desktopNavRef}
          className="hidden items-center gap-6 text-sm text-gray-600 lg:flex"
          onMouseLeave={closeDesktop}
          onBlur={(e) => {
            if (!desktopNavRef.current?.contains(e.relatedTarget as Node)) closeDesktop();
          }}
        >
          {links.map((l) => {
            if (l.children) {
              const open = desktopMenu === l.label;
              return (
                <div
                  key={l.label}
                  className="relative"
                  onMouseEnter={() => {
                    setDesktopMenu(l.label);
                    setDesktopSubMenu(null);
                  }}
                >
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={() => setDesktopMenu(open ? null : l.label)}
                    className="flex items-center gap-1 py-1 transition-colors hover:text-gray-900 focus-visible:text-gray-900 focus-visible:outline-none"
                  >
                    {l.label}
                    <span className={`transition-opacity ${open ? "opacity-100" : "opacity-50"}`}>
                      <ChevronDown />
                    </span>
                  </button>

                  {/* Level 2 dropdown */}
                  <div
                    className={`absolute top-full left-1/2 z-50 min-w-80 -translate-x-1/2 pt-3 transition-all duration-150 ${open ? "visible opacity-100" : "invisible opacity-0"}`}
                  >
                    <div role="menu" className="rounded-xl border border-gray-100 bg-white py-1.5 shadow-xl">
                      {l.children.map((child, i) => {
                        if (isNavGroup(child)) {
                          const subOpen = desktopSubMenu === child.label;
                          return (
                            // Level 2 group → clic/hover muestra el flyout de nivel 3
                            <div
                              key={child.label}
                              className="relative"
                              onMouseEnter={() => setDesktopSubMenu(child.label)}
                            >
                              <button
                                type="button"
                                aria-haspopup="menu"
                                aria-expanded={subOpen}
                                onClick={() => setDesktopSubMenu(subOpen ? null : child.label)}
                                className="mx-1 flex w-[calc(100%-0.5rem)] items-center justify-between gap-8 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
                              >
                                <span className="whitespace-nowrap">{child.label}</span>
                                <span className={`shrink-0 transition-colors ${subOpen ? "text-gray-600" : "text-gray-400"}`}>
                                  <ChevronRight />
                                </span>
                              </button>

                              {/* Level 3 flyout */}
                              <div
                                className={`absolute left-full top-0 z-50 pl-2 transition-all duration-100 ${subOpen ? "visible opacity-100" : "invisible opacity-0"}`}
                              >
                                <div
                                  role="menu"
                                  ref={(el) => { subFlyoutRefs.current[child.label] = el; }}
                                  className="min-w-64 overflow-y-auto whitespace-nowrap rounded-xl border border-gray-100 bg-white py-1.5 shadow-xl"
                                >
                                  {child.children.map((sub) => (
                                    <NavLink
                                      key={sub.href}
                                      to={sub.href}
                                      role="menuitem"
                                      onClick={closeDesktop}
                                      className={({ isActive }) =>
                                        `block px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-rose-50 font-medium text-rose-700" : "text-gray-700 hover:bg-gray-50"}`
                                      }
                                    >
                                      {sub.label}
                                    </NavLink>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Leaf item — add separator after the last group
                        const prevIsGroup = i > 0 && isNavGroup(l.children![i - 1]);
                        return (
                          <React.Fragment key={child.href}>
                            {prevIsGroup && <div className="mx-3 my-1 h-px bg-gray-100" />}
                            <NavLink
                              to={child.href}
                              role="menuitem"
                              onClick={closeDesktop}
                              className={({ isActive }) =>
                                `block px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-rose-50 font-medium text-rose-700" : "text-gray-700 hover:bg-gray-50"}`
                              }
                            >
                              {child.label}
                            </NavLink>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            if (!l.href) return null;
            return l.href.startsWith("/") ? (
              <NavLink
                key={l.href}
                to={l.href}
                end
                className={({ isActive }) =>
                  isActive ? "text-rose-600 underline underline-offset-8" : "hover:text-gray-900"
                }
              >
                {l.label}
              </NavLink>
            ) : (
              <a key={l.href} href={l.href} className="hover:text-gray-900">
                {l.label}
              </a>
            );
          })}

          {user?.isAdmin && (
            <div
              className="relative"
              onMouseEnter={() => {
                setDesktopMenu("__admin__");
                setDesktopSubMenu(null);
              }}
            >
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={desktopMenu === "__admin__"}
                onClick={() => setDesktopMenu(desktopMenu === "__admin__" ? null : "__admin__")}
                className="flex items-center gap-1 rounded-md bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
              >
                Admin
                <span className={`transition-opacity ${desktopMenu === "__admin__" ? "opacity-100" : "opacity-50"}`}>
                  <ChevronDown />
                </span>
              </button>
              <div
                className={`absolute top-full right-0 z-50 min-w-48 pt-2 transition-all duration-150 ${desktopMenu === "__admin__" ? "visible opacity-100" : "invisible opacity-0"}`}
              >
                <div role="menu" className="rounded-xl border border-gray-100 bg-white py-1.5 shadow-xl">
                  {ADMIN_NAV.map((item) => {
                    if (isNavGroup(item)) {
                      const subOpen = desktopSubMenu === item.label;
                      return (
                        // Sección → flyout hacia la izquierda con sus enlaces
                        <div
                          key={item.label}
                          className="relative"
                          onMouseEnter={() => setDesktopSubMenu(item.label)}
                        >
                          <button
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={subOpen}
                            onClick={() => setDesktopSubMenu(subOpen ? null : item.label)}
                            className="mx-1 flex w-[calc(100%-0.5rem)] items-center justify-between gap-8 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
                          >
                            <span>{item.label}</span>
                            <span className={`shrink-0 rotate-180 transition-colors ${subOpen ? "text-gray-600" : "text-gray-400"}`}>
                              <ChevronRight />
                            </span>
                          </button>

                          <div
                            className={`absolute right-full top-0 z-50 pr-2 transition-all duration-100 ${subOpen ? "visible opacity-100" : "invisible opacity-0"}`}
                          >
                            <div role="menu" className="min-w-52 rounded-xl border border-gray-100 bg-white py-1.5 shadow-xl">
                              {item.children.map((sub) => (
                                <NavLink
                                  key={sub.href}
                                  to={sub.href}
                                  role="menuitem"
                                  onClick={closeDesktop}
                                  className={({ isActive }) =>
                                    `block px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-rose-50 font-medium text-rose-700" : "text-gray-700 hover:bg-gray-50"}`
                                  }
                                >
                                  {sub.label}
                                </NavLink>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        role="menuitem"
                        onClick={closeDesktop}
                        className={({ isActive }) =>
                          `block px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-rose-50 font-medium text-rose-700" : "text-gray-700 hover:bg-gray-50"}`
                        }
                      >
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user && <ReferidosLink />}
          {user && <NotificationBell />}
          {user && (
            <Link to="/perfil" aria-label="Mi perfil" className="lg:hidden">
              {user.avatarUrl && !avatarError ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name}
                  referrerPolicy="no-referrer"
                  width={36}
                  height={36}
                  className="h-9 w-9 rounded-full object-cover"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-600 text-sm font-semibold text-white">
                  {user.name.trim().slice(0, 1).toUpperCase()}
                </div>
              )}
            </Link>
          )}
          <button
            type="button"
            aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-100 text-gray-700 hover:bg-gray-50 lg:hidden"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              {mobileOpen ? <path d="M18 6 6 18M6 6l12 12" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>

          <div className="hidden lg:flex lg:items-center lg:gap-3">
            {user ? (
              <Link to="/perfil" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <div className="hidden flex-col sm:flex">
                  {user.daysLeft != null && (
                    <span className={`text-xs font-medium ${user.daysLeft <= 3 ? "text-rose-600" : user.daysLeft <= 7 ? "text-amber-600" : "text-emerald-600"}`}>
                      {textoVigencia(user.daysLeft, user.hoursLeft, user.minutesLeft)}
                    </span>
                  )}
                </div>
                {user.avatarUrl && !avatarError ? (
                  <img src={user.avatarUrl} alt={user.name} referrerPolicy="no-referrer" width={36} height={36} className="h-9 w-9 rounded-full object-cover" onError={() => setAvatarError(true)} />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-600 text-sm font-semibold text-white">
                    {user.name.trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
              </Link>
            ) : null}
            {user && (
              <Form method="post" action="/logout">
                <button
                  type="submit"
                  aria-label="Cerrar sesión"
                  title="Cerrar sesión"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-50 hover:text-rose-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" x2="9" y1="12" y2="12" />
                  </svg>
                </button>
              </Form>
            )}
            {!user && (
              <>
                <Link to="/login"><Button variant="secondary" type="button">Ingresar</Button></Link>
                <Link to="/registro"><Button variant="primary" type="button">Comenzar</Button></Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-gray-100 bg-white lg:hidden">
          <div className="mx-auto w-full max-w-6xl px-4 py-3">
            <div className="flex flex-col gap-1">
              {links.map((l) => {
                if (l.children) {
                  const open = mobileDropdown === l.label;
                  return (
                    <div key={l.label}>
                      <button
                        type="button"
                        onClick={() => toggleMobileDropdown(l.label)}
                        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {l.label}
                        <span className={`transition-transform ${open ? "rotate-180" : ""}`}>
                          <ChevronDown />
                        </span>
                      </button>

                      {open && (
                        <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-gray-100 pl-3">
                          {l.children.map((child) => {
                            if (isNavGroup(child)) {
                              const subOpen = mobileSubDropdown === child.label;
                              return (
                                <div key={child.label}>
                                  <button
                                    type="button"
                                    onClick={() => setMobileSubDropdown(subOpen ? null : child.label)}
                                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                  >
                                    {child.label}
                                    <span className={`transition-transform ${subOpen ? "rotate-180" : ""}`}>
                                      <ChevronDown />
                                    </span>
                                  </button>
                                  {subOpen && (
                                    <div className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-gray-100 pl-3">
                                      {child.children.map((sub) => (
                                        <NavLink
                                          key={sub.href}
                                          to={sub.href}
                                          onClick={closeMobile}
                                          className={({ isActive }) =>
                                            `block rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-rose-50 font-medium text-rose-700" : "text-gray-600 hover:bg-gray-50"}`
                                          }
                                        >
                                          {sub.label}
                                        </NavLink>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <NavLink
                                key={child.href}
                                to={child.href}
                                onClick={closeMobile}
                                className={({ isActive }) =>
                                  `rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-rose-50 font-medium text-rose-700" : "text-gray-600 hover:bg-gray-50"}`
                                }
                              >
                                {child.label}
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                if (!l.href) return null;
                return l.href.startsWith("/") ? (
                  <NavLink
                    key={l.href}
                    to={l.href}
                    end
                    onClick={closeMobile}
                    className={({ isActive }) =>
                      isActive
                        ? "rounded-lg bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700"
                        : "rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    }
                  >
                    {l.label}
                  </NavLink>
                ) : (
                  <a key={l.href} href={l.href} onClick={closeMobile} className="rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {l.label}
                  </a>
                );
              })}

              <div className="my-1 h-px w-full bg-gray-100" />

              {user ? (
                <>
                  {user.isAdmin &&
                    ADMIN_NAV.map((item) => {
                      if (isNavGroup(item)) {
                        const open = mobileAdminSection === item.label;
                        return (
                          <div key={item.label}>
                            <button
                              type="button"
                              onClick={() => setMobileAdminSection(open ? null : item.label)}
                              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50"
                            >
                              {item.label}
                              <span className={`transition-transform ${open ? "rotate-180" : ""}`}>
                                <ChevronDown />
                              </span>
                            </button>
                            {open && (
                              <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-gray-100 pl-3">
                                {item.children.map((sub) => (
                                  <NavLink
                                    key={sub.href}
                                    to={sub.href}
                                    onClick={closeMobile}
                                    className={({ isActive }) =>
                                      `rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-rose-50 font-medium text-rose-700" : "text-gray-600 hover:bg-gray-50"}`
                                    }
                                  >
                                    {sub.label}
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <NavLink
                          key={item.href}
                          to={item.href}
                          onClick={closeMobile}
                          className={({ isActive }) =>
                            `rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                              isActive ? "bg-rose-50 text-rose-700" : "text-rose-600 hover:bg-rose-50"
                            }`
                          }
                        >
                          {item.label}
                        </NavLink>
                      );
                    })}
                  <Form method="post" action="/logout">
                    <button type="submit" className="w-full rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                      Cerrar sesión
                    </button>
                  </Form>
                </>
              ) : (
                <>
                  <a href="/login" onClick={closeMobile} className="rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Ingresar</a>
                  <Link to="/registro" onClick={closeMobile} className="rounded-lg bg-rose-600 px-3 py-2 text-left text-sm font-semibold text-white hover:bg-rose-700">
                    Comenzar
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
